import { FC } from 'react';
import { css } from '@emotion/react';
import { Channel } from '@weave/schema-gen-ts/dist/schemas/comm-preference/shared/v1/enums.pb';
import { MessageType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { DataSourcesHooks } from '@frontend/api-data-sources';
import { CommPreferenceQueries } from '@frontend/api-messaging';
import { PersonTypes } from '@frontend/api-person';
import { AdaptoActions } from '@frontend/adapto-actions';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { Actions } from '@frontend/contact-actions';
import { useTranslation } from '@frontend/i18n';
import { useMerchants } from '@frontend/payments-hooks';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { useAlert } from '@frontend/design-system';

interface ContactActionsProps {
  onCall?: () => void;
  person: PersonTypes.Person;
  locationId?: string;
}

export const ContactActions: FC<React.PropsWithChildren<ContactActionsProps>> = ({ person, locationId }) => {
  const { selectedLocationIds, getLocationName } = useAppScopeStore();
  const { t } = useTranslation('contacts');
  const singleLocationId = selectedLocationIds[0];
  const groupId = locationId || singleLocationId;
  const alert = useAlert();

  const hasACL = hasSchemaACL(groupId, Permission.REVIEW_READ);

  const { getLocationsByDatasource } = DataSourcesHooks.useDatasourceToLocations(selectedLocationIds, getLocationName);
  const locations = getLocationsByDatasource(person.SourceID, person.ClientLocationID);

  const { firstActiveMerchantLocationId, isLoading } = useMerchants(groupId);
  const canShowRequestPaymentAction = firstActiveMerchantLocationId && !isLoading;

  const { data: preferenceLinks } = CommPreferenceQueries.useGenerateManagementUrls({
    locationId,
    channel: Channel.CHANNEL_SMS,
    userChannelAddress: formatPhoneNumberE164(person.MobilePhone || person.HomePhone),
    messageType: MessageType_Enum.MESSAGING_MANUAL,
    includeVoice: true,
  });

  return (
    <AdaptoActions
      maxActions={5}
      variant='secondary-buttons'
      css={css`
        .action-item--secondary {
          padding-left: ${theme.spacing(1)};
          padding-right: ${theme.spacing(1)};
        }
      `}
    >
      {canShowRequestPaymentAction && (
        <Actions.ContactPayments
          context={{ person, personId: person.PersonID, locationId: groupId }}
          trackingIdSuffix='profile-contact-actions'
        />
      )}

      <Actions.Message
        label={t('Message')}
        context={{
          personId: person.PersonID,
          threadGroupId: locationId,
          groupIds: locationId ? [locationId] : undefined,
        }}
        trackingIdSuffix='contact-panel'
      />

      <Actions.Call label={t('Call')} context={{ person }} trackingIdSuffix='profile-contact-actions' />

      <Actions.SendForm
        label={t('Send Form')}
        context={{
          locationId: singleLocationId,
          person,
        }}
        trackingIdSuffix='profile-contact-actions'
      />

      <Actions.QuickFill
        label={t('Add to Quick Fill')}
        personId={person.PersonID}
        locations={locations}
        trackingIdSuffix='profile-contact-actions'
      />

      {hasACL && (
        <Actions.Review
          label={t('Review')}
          groupId={groupId}
          personId={person.PersonID}
          trackingIdSuffix='-profile-contact-actions'
        />
      )}
      <Actions.CommunicationPreferences
        onClick={() => {
          const { managementUrl } = preferenceLinks ?? {};
          if (managementUrl) {
            window.open(`${preferenceLinks?.managementUrl}&ref=nwx`, '_blank', 'noopener noreferrer');
          } else {
            alert.error(t('Error opening communication preferences. Try again.'));
          }
        }}
        label={t('Communication Preferences')}
        trackingId='communication-preferences'
      />
    </AdaptoActions>
  );
};
